import { html, nothing } from 'lit';
import type { PudoLocation } from '../../db/PudoLocation.js';
import type { ServiceNowTicket } from '../../db/ServiceNowTicket.js';
import * as m from '../../localization/out/messages.js';
import { googleMapsUrl } from './_colleague-format.js';

/**
 * The "Actions" menu shared by the FE's own Tickets list and the Colleagues list: download the addresses of the
 * tickets on screen, the NSC / Dell links, and the PUDO depots. It is the app's version of the legacy tool's menu.
 */

/** Static entries of the bulk-actions menu. */
const NSC_TICKETS_URL = 'https://tickets.nscglobal.com/';
const DELL_TECH_SUPPORT_URL = 'https://www.dell.com/';

/**
 * The Dell group of the actions menu.
 *
 * DDU is Dell Domain Utilities; DFSM is Dell Field Services Mobile, the app Dell's own engineers
 * use on site. The two store links are the exact targets `dfsm.dell.com/download` opens from its
 * Android / iOS buttons — Dell distributes the app through App Center rather than through the
 * public stores, so there is no Play Store or App Store page to link instead. Read off that page's
 * own `SelectedAndroidApp()` / `SelectedIosApp()` handlers on 2026-09-21, both verified to resolve.
 */
const DELL_LINKS = [
  {
    url: 'https://tickets.nscglobal.com/pstddu/home/',
    // Written out in full rather than composed from a name: Tailwind scans the source for complete
    // class names, so an interpolated `icon-[material-symbols--${…}]` would generate nothing.
    iconClass: 'icon-[material-symbols--inventory-2-outline]',
    label: () => m.colleague_action_ddu(),
  },
  {
    url: 'https://dfsm.dell.com/login',
    iconClass: 'icon-[material-symbols--language]',
    label: () => m.colleague_action_dfsm_web(),
  },
  {
    url: 'https://install.appcenter.ms/orgs/dellfieldservicesmobile/apps/dfsm-prod/distribution_groups/public',
    iconClass: 'icon-[material-symbols--android]',
    label: () => m.colleague_action_dfsm_android(),
  },
  {
    url: 'https://install.appcenter.ms/orgs/dellfieldservicesmobile/apps/dfsm-prod-1/distribution_groups/public',
    iconClass: 'icon-[material-symbols--phone-iphone]',
    label: () => m.colleague_action_dfsm_ios(),
  },
] as const;

/**
 * Partner companies whose engineers see the Dell group.
 *
 * Matched as a case-insensitive substring of the company name so "NSC Dell", "NSC Global" and
 * "Dell Services" all qualify. An engineer with no partner company sees no Dell links — there is
 * nothing to match against, and these are internal tools rather than public pages.
 */
const DELL_COMPANY_MARKERS = ['dell', 'nsc'];

export function showsDellLinks(companyName: string | null | undefined): boolean {
  const name = (companyName ?? '').toLowerCase();
  return name !== '' && DELL_COMPANY_MARKERS.some(marker => name.includes(marker));
}

/** How many depots the actions menu lists before it stops (the full set is on the map). */
const PUDO_MENU_LIMIT = 12;

/** Build the address CSV the "Download addresses" action hands over. */
function addressesCsv(tickets: ServiceNowTicket[]): string {
  const cell = (value: string | null | undefined) => `"${(value ?? '').replace(/"/g, '""')}"`;
  const header = [
    'Ticket',
    'Field Engineer',
    'Customer',
    'Address',
    'Latitude',
    'Longitude',
    'Window start',
    'Window end',
  ];
  const rows = tickets.map(t =>
    [
      cell(t.number),
      cell(t.assignedToName),
      cell(t.clientName),
      cell(t.locationLabel),
      cell(t.locationLat?.toString()),
      cell(t.locationLng?.toString()),
      cell(t.windowStart),
      cell(t.windowEnd),
    ].join(','),
  );
  return [header.join(','), ...rows].join('\n');
}

function downloadCsv(filename: string, contents: string): void {
  const blob = new Blob([contents], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export interface ActionsMenuOptions {
  /** The tickets on screen — what "Download addresses" exports. */
  tickets: ServiceNowTicket[];
  /** Depots from ServiceNow, listed as map links. */
  pudos: PudoLocation[];
  /** The Dell group (DDU + DFSM) is for engineers at a Dell / NSC company only. */
  showDellLinks: boolean;
  /** File name for the addresses download. */
  csvFilename: string;
}

// "Download Addresses", "DDU" and "Dell Tech Support" were specified; nscTickets and the depot links are the two
// other entries from the legacy menu whose targets are unambiguous. The Dell group (DDU + DFSM) only renders for
// engineers at a Dell/NSC partner company. "List CADs" and "Search Ticket" are covered by the reply-code filter
// (pending) and by the search box.
export function ActionsMenu({ tickets, pudos, showDellLinks, csvFilename }: ActionsMenuOptions) {
  return html`
    <div class="dropdown dropdown-end">
      <button tabindex="0" class="btn btn-sm btn-outline gap-1">
        <span class="icon-[material-symbols--menu] text-base"></span>
        ${m.colleague_actions()}
      </button>
      <ul tabindex="0" class="dropdown-content menu bg-base-100 rounded-box z-30 w-64 p-2 shadow border border-base-300">
        <li>
          <button ?disabled=${tickets.length === 0} @click=${() => downloadCsv(csvFilename, addressesCsv(tickets))}>
            <span class="icon-[material-symbols--download] text-base"></span>
            ${m.colleague_action_download_addresses()}
          </button>
        </li>
        <li>
          <a href=${NSC_TICKETS_URL} target="_blank" rel="noopener noreferrer">
            <span class="icon-[material-symbols--confirmation-number-outline] text-base"></span>
            ${m.colleague_action_nsctickets()}
          </a>
        </li>
        <li>
          <a href=${DELL_TECH_SUPPORT_URL} target="_blank" rel="noopener noreferrer">
            <span class="icon-[material-symbols--call] text-base"></span>
            ${m.colleague_action_dell_support()}
          </a>
        </li>
        ${showDellLinks
          ? html`
              <li class="menu-title text-xs">${m.colleague_action_dell_group()}</li>
              ${DELL_LINKS.map(
                link => html`
                  <li>
                    <a href=${link.url} target="_blank" rel="noopener noreferrer">
                      <span class="${link.iconClass} text-base"></span>
                      ${link.label()}
                    </a>
                  </li>
                `,
              )}
            `
          : nothing}
        ${pudos.length > 0
          ? html`
              <li class="menu-title text-xs">${m.colleague_action_pudos()}</li>
              ${pudos
                .filter(p => typeof p.lat === 'number' && typeof p.lng === 'number')
                .slice(0, PUDO_MENU_LIMIT)
                .map(
                  p => html`
                    <li>
                      <a href=${googleMapsUrl(p.lat!, p.lng!)} target="_blank" rel="noopener noreferrer" class="text-xs">
                        <span class="icon-[material-symbols--warehouse-outline] text-base"></span>
                        ${p.name}
                      </a>
                    </li>
                  `,
                )}
            `
          : nothing}
      </ul>
    </div>
  `;
}
